// Etiquetas e iconos de la navegación. NADA DE PERMISOS (HU-32a, MG-ILE-63 · tarea 32a-6).
//
// Hasta el Sprint 3 este archivo decidía qué módulos veía cada rol (`PERMISOS`) y dónde
// aterrizaba (`LANDING`), con códigos propios (`regulatorio`, `reppais`, `admin`) distintos de
// los de la tabla `rol`. Era una segunda copia de la matriz `rol_modulo`, y la pantalla elegía
// la suya: el menú ofrecía pantallas que el servidor negaba (bugs 906, 714 y 900, O-07).
//
// Desde HU-32a el menú y el aterrizaje llegan de `GET /sesion` (`modulos_permitidos`,
// `modulo_inicial`; `estandares_frontend.md` §8.1 R4). Aquí queda solo el GLOSARIO: cómo se
// llama y con qué icono se pinta cada módulo de la tabla `modulo`, y el tono del chip de cada
// rol de la tabla `rol`. Los códigos son los de la base.

/**
 * `disponible: false` marca los módulos que el menú lista pero que todavía no tienen pantalla.
 * El menú los pinta con el chip «Sprint 3» y `aria-disabled`, y al abrirlos la aplicación
 * explica qué se puede hacer hoy en vez de mostrar una cola vacía.
 */
export const MODULOS = {
  dashboard: { label: 'Dashboard', icon: 'LayoutDashboard', disponible: true },
  bandeja: { label: 'Bandeja de casos', icon: 'Inbox', disponible: true },
  registro: { label: 'Registrar caso', icon: 'FilePlus2', disponible: true },
  evaluacion: { label: 'Evaluación del Comité', icon: 'Stethoscope', disponible: true },
  aprobacion: { label: 'Numeración y aprobación', icon: 'FileCheck2', disponible: true },
  notificacion: { label: 'Notificación regulatoria', icon: 'Send', disponible: true },
  seguimiento: { label: 'Disposiciones y seguimiento', icon: 'ListChecks', disponible: true },
  indicadores: { label: 'Indicadores', icon: 'ChartLine', disponible: true },
  admin: { label: 'Administración', icon: 'Settings', disponible: true },
};

/** Los módulos que la aplicación sabe pintar. Derivado de `MODULOS`, no escrito a mano. */
export type ModuloDeNavegacion = keyof typeof MODULOS;

/** `true` si el código que mandó el servidor es un módulo que la aplicación sabe pintar. */
export const esModuloConocido = (codigo: unknown): codigo is ModuloDeNavegacion =>
  typeof codigo === 'string' && Object.hasOwn(MODULOS, codigo);

/**
 * Los módulos del menú, en el orden de `MODULOS` (el de la tabla `modulo`), a partir de la lista
 * del servidor. Un código que la aplicación no conoce no se pinta: no tiene pantalla ni icono, y
 * se trata como vacío del contrato (no se inventa una entrada).
 */
export const modulosDelMenu = (permitidos: readonly string[]): ModuloDeNavegacion[] =>
  (Object.keys(MODULOS) as ModuloDeNavegacion[]).filter((k) => permitidos.includes(k));

// `as const` fija los tonos como literales ('info', 'ai', ...) en vez de ensancharlos a
// `string`: así encajan donde se espera un tono del sistema de diseño.
//
// Las etiquetas son las de los botones de la ENTRADA SIMULADA de desarrollo (las usan las E2E,
// `e2e/apoyo/ui.ts`). Lo que la aplicación muestra como nombre del rol en la cabecera y en
// Usuarios es el `texto_del_rol` del servidor, no esta etiqueta.
export const ROLES = {
  registrador: { label: 'Registrador', desc: 'Rep. Comercial / Brand Manager', tone: 'info' },
  comite: { label: 'Comité de FV', desc: 'Evaluación técnica', tone: 'ai' },
  asuntos_regulatorios: { label: 'Asuntos Regulatorios', desc: 'Numeración y notificación', tone: 'success' },
  aprobador: { label: 'VP / Gerencia', desc: 'Aprobación del informe', tone: 'warning' },
  representante_pais: { label: 'Representante del País', desc: 'Trámite ante la autoridad local', tone: 'neutral' },
  administrador: { label: 'Administrador', desc: 'Catálogos, reglas y usuarios', tone: 'danger' },
  oficial_cumplimiento: { label: 'Oficial de cumplimiento', desc: 'Auditoría y dato L2, en solo lectura', tone: 'neutral' },
  especialista: { label: 'Especialista invitado', desc: 'Solo lectura del caso al que lo invitaron', tone: 'neutral' },
} as const;

/** Los roles que la aplicación reconoce: los códigos de la tabla `rol`. */
export type RolDeNavegacion = keyof typeof ROLES;

/** El tono del chip de un rol; un código desconocido va en neutro (el texto lo da el servidor). */
export const tonoDelRol = (rol: string) =>
  (Object.hasOwn(ROLES, rol) ? ROLES[rol as RolDeNavegacion].tone : 'neutral');
